import { ReasonChips, TextArea } from '../kit.js';
import type { ReactNode } from 'react';
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { reasonPresetsForIntent } from '../reason-presets.js';

/** Reason with suggestion chips; label is always «دلیل» unless optional (XAP-09). */
export function ReasonField({
  error,
  intent,
  label = 'دلیل',
  maxLength = 1000,
  onChange,
  optional = false,
  presets,
  value,
  ...rest
}: {
  readonly error?: string | undefined;
  readonly intent?: string;
  readonly label?: string;
  readonly maxLength?: number;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly optional?: boolean;
  readonly presets?: readonly string[];
  readonly value: string;
  readonly name?: string;
}) {
  const chips = presets ?? (intent === undefined ? [] : reasonPresetsForIntent(intent));
  const fieldLabel: ReactNode = optional ? `${label} (اختیاری)` : label;

  return (
    <div className="admin-reason-field">
      <TextArea
        error={error}
        label={fieldLabel}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        value={value}
        {...rest}
      />
      {/* Suggestions sit under the field they fill (W4, as in merchant-web). */}
      {chips.length === 0 ? null : (
        <ReasonChips onChange={onChange} reasons={chips} value={value} />
      )}
    </div>
  );
}

/**
 * Reason suggestions under a registered reason field of a React Hook Form form (W4: most reason
 * fields had no suggestion chips). A chip appends its text to the field.
 */
export function FormReasonChips<F extends FieldValues, O>({
  form,
  intent,
  name = 'reason' as Path<F>,
}: {
  readonly form: UseFormReturn<F, unknown, O>;
  readonly intent: string;
  readonly name?: Path<F>;
}) {
  const value: unknown = useWatch({ control: form.control, name });
  return (
    <ReasonChips
      onChange={(next) =>
        form.setValue(name, next as PathValue<F, Path<F>>, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
      reasons={reasonPresetsForIntent(intent)}
      value={typeof value === 'string' ? value : ''}
    />
  );
}
